import React from 'react';
import PropTypes from 'prop-types';
import './Price.css';

/**
 * Price Component
 * 
 * Displays prices in various sizes/contexts with support for
 * discounted and included states.
 * 
 * Figma parent node: 3799:17093
 * 
 * Variants:
 * | Type          | State      | nodeId       |
 * |---------------|------------|--------------|
 * | xx-large      | default    | 3799:17157   |
 * | x-large       | default    | 3799:17172   |
 * | x-large       | discounted | 3799:17230   |
 * | x-large       | included   | 3799:17165   |
 * | large         | default    | 3799:17243   |
 * | large         | discounted | 3816:1131    |
 * | shopping-cart | default    | 3799:17303   |
 * | shopping-cart | discounted | 3799:17279   |
 * | shopping-cart | included   | 3799:17247   |
 * | medium        | default    | 3799:17372   |
 * | medium        | discounted | 3799:17325   |
 * | medium        | included   | 3799:17311   |
 * | package       | default    | 3799:17376   |
 * | package       | discounted | 3799:17336   |
 * | package       | included   | 3799:17318   |
 * | regular       | default    | 3799:17390   |
 * | regular       | discounted | 3799:17384   |
 * | regular       | included   | 3944:14536   |
 * | small         | default    | 3799:17398   |
 * | small         | discounted | 3799:17395   |
 * | small         | included   | 3799:17392   |
 */
const Price = ({
  type = 'small',
  state = 'default',
  price = '',
  originalPrice = '',
  badgeText = 'Included',
  savingsText = '',
  className = '',
}) => {
  const rootClasses = [
    'ds-price',
    `ds-price--${type}`,
    `ds-price--${state}`,
    className,
  ].filter(Boolean).join(' ');

  // Badge sub-element (Included state)
  const Badge = () => (
    <div className="ds-price__badge">
      <span className="ds-price__badge-text typo-h6">{badgeText}</span>
    </div>
  );

  // Original / struck-through price
  const OriginalPrice = () => (
    <span className="ds-price__original">{originalPrice}</span>
  );

  /* ------ Default state: just the price ------ */
  if (state === 'default') {
    return (
      <div className={rootClasses}>
        <span className="ds-price__amount">{price}</span>
      </div>
    );
  }

  /* ------ Included state ------ */
  if (state === 'included') {
    // x-large: green price + badge below
    if (type === 'x-large') {
      return (
        <div className={rootClasses}>
          <span className="ds-price__amount">{price}</span>
          <Badge />
        </div>
      );
    }

    // shopping-cart: badge + prices row (current + original)
    if (type === 'shopping-cart') {
      return (
        <div className={rootClasses}>
          <Badge />
          <div className="ds-price__prices">
            <span className="ds-price__amount">{price}</span>
            {originalPrice && <OriginalPrice />}
          </div>
        </div>
      );
    }

    // regular: green price + strikethrough original + badge (row)
    if (type === 'regular') {
      return (
        <div className={rootClasses}>
          <span className="ds-price__amount">{price}</span>
          {originalPrice && <OriginalPrice />}
          <Badge />
        </div>
      );
    }

    // medium, small, package: just the badge
    return (
      <div className={rootClasses}>
        <Badge />
      </div>
    );
  }

  /* ------ Discounted state ------ */
  if (state === 'discounted') {
    // Package: price | separator | old price + savings
    if (type === 'package') {
      return (
        <div className={rootClasses}>
          <span className="ds-price__amount">{price}</span>
          <div className="ds-price__separator" />
          <div className="ds-price__details">
            <OriginalPrice />
            {savingsText && (
              <span className="ds-price__savings">{savingsText}</span>
            )}
          </div>
        </div>
      );
    }

    // x-large, shopping-cart: column (price on top, old below)
    // large, medium, regular, small: row (price + original)
    return (
      <div className={rootClasses}>
        <span className="ds-price__amount">{price}</span>
        <OriginalPrice />
      </div>
    );
  }

  return null;
};

Price.propTypes = {
  /** Size / context variant */
  type: PropTypes.oneOf([
    'small', 'regular', 'medium', 'large',
    'x-large', 'xx-large', 'shopping-cart', 'package', 'editorial',
  ]),
  /** Price display state */
  state: PropTypes.oneOf(['default', 'discounted', 'included']),
  /** Current / actual price text */
  price: PropTypes.string,
  /** Original price (shown as strikethrough in discounted state) */
  originalPrice: PropTypes.string,
  /** Badge text for included state */
  badgeText: PropTypes.string,
  /** Savings percentage text (Package discounted only) */
  savingsText: PropTypes.string,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default Price;
